import { locale } from '../lib/locale';

/**
 * Days, times and events (1.19): calendar page, event details, the day plan, the wall reminder.
 * German is the master; English has the same keys.
 */
export const CALENDAR_TEXT = {
  de: {
    today: 'Heute',
    tomorrow: 'Morgen',
    todayLower: 'heute',
    happeningNow: 'läuft gerade',
    inMin: (n: string) => `in ${n} Min.`,
    inHours: (h: string) => `in ${h} Std.`,
    inHoursMin: (h: string, m: string) => `in ${h} Std. ${m} Min.`,
    inMinutesCap: (n: number) => (n === 1 ? 'In 1 Minute' : `In ${n} Minuten`),
    tomorrowAt: (time: string) => `Morgen ${time}`,
    bring: (items: string) => `Mitnehmen: ${items}`,
    pickDay: 'Tag wählen',
    addEvent: 'Termin',
    noEvents: 'Keine Termine – ein freier Tag.',
    nextTwoWeeks: 'Die nächsten zwei Wochen',
    newEvent: 'Neuer Termin',
    editTimetable: 'Stundenplan bearbeiten',
    editEvent: 'Termin bearbeiten',
    allDay: 'ganztags',
    wholeFamily: 'Ganze Familie',
    // an event with stars is ticked off like a task (1.25, Mike 20.10.)
    done: 'Erledigt?',
    route: 'Route',
    doneHint: 'Abhaken bringt Sterne – heute oder noch bis morgen.',
    timetable: 'Stundenplan',
    waste: 'Müllabfuhr',
    calendarLink: 'Kalender-Link',
    from: (source: string) => `Aus: ${source}`,
    edit: 'Bearbeiten',
    reallyDelete: 'Wirklich löschen',
    // a series: only this date or the whole series (Mike, 19.10.)
    deleteOne: (day: string) => `Nur am ${day} löschen`,
    deleteFollowing: (day: string) => `Ab ${day} alle löschen`,
    deleteSeries: 'Ganze Serie löschen',
    delete: 'Löschen',
    reminder: 'Erinnerung',
    onlyTimes: 'Nur Zeiten eingetragen – Fächer können Eltern ergänzen.',
    /** default names of timetables, as stored – shown translated */
    school: 'Schule',
    kita: 'Kita',
    ogs: 'OGS',
    // Kalender 1 (1.24)
    birthdayOf: (name: string) => `Geburtstag: ${name}`,
    views: 'Ansicht',
    viewDay: 'Tag',
    viewWeek: 'Woche',
    viewMonth: 'Monat',
    viewYear: 'Jahr',
    back: 'Zurück',
    forward: 'Weiter',
    week: (n: number) => `KW ${n}`,
    openWeek: (n: number) => `Kalenderwoche ${n} öffnen`,
    whoWhereWhen: 'Wer ist wann wo?',
    family: 'Familie',
    me: (name: string) => `${name} (ich)`,
    monthOpen: 'Monat aufklappen',
    monthClose: 'Monat zuklappen',
    dayEvents: (day: string, n: number) => (n === 1 ? `${day}, 1 Termin` : `${day}, ${n} Termine`),
    more: (n: number) => `+${n} weitere`,
    freeDay: 'frei',
    search: 'Suchen',
    searchLabel: 'Termine suchen',
    searchHint: 'Sucht in allen Terminen – Titel, Ort und Person.',
    searchDone: 'Fertig',
    noHits: (q: string) => `Nichts gefunden für „${q}“.`,
    upcoming: 'Demnächst',
    earlier: 'Vergangen',
    freeWeekends: 'Freie Wochenenden',
    freeWeekendsHint: 'für alle, die gerade gezeigt werden',
    weekendFree: 'Ganzes Wochenende frei',
    saturdayFree: 'Samstag frei',
    sundayFree: 'Sonntag frei',
    soon: 'Bald',
    yearHint: 'Je dunkler ein Tag, desto mehr Termine. Gelb: Ferien. Monat antippen öffnet ihn.',
    openMonth: (month: string) => `${month} öffnen`,
    layers: 'Was steht im Kalender?',
    layersHint: 'Jedes Gerät merkt sich seine Auswahl – an der Wand zum Beispiel ohne Müll.',
    layerHolidays: 'Ferien und Feiertage',
    layerBirthdays: 'Geburtstage',
    layerSchool: 'Schule und Kita',
    layerShifts: 'Schichten',
    shift: 'Schicht',
    vacation: 'Urlaub',
    oneOffJob: 'Aufgabe',
    shiftOnlyToday: 'Nur an diesem Tag',
    shiftOnlyTodayHint:
      'Schicht getauscht, krank oder Urlaub? Der Rhythmus läuft danach einfach weiter.',
    shiftFree: 'Frei',
    shiftAsPlanned: 'Wie im Rhythmus',
    shiftEditPlan: 'Schichtplan ändern',
    shiftEndPlan: 'Plan ab hier beenden',
    shiftSwapped: (planned: string) => `Getauscht – laut Rhythmus: ${planned}`,
    layerWaste: 'Müllabfuhr',
    layerSubscriptions: 'Abonnierte Kalender',
    noRegion: 'Ferien und Feiertage fehlen – unter Einstellungen → Familie den Wohnort neu wählen.',
    toSettings: 'Zu den Einstellungen',
    todayTodo: 'Heute zu tun',
    allTasks: 'Alle Aufgaben',
    nothingTodo: 'Heute ist nichts mehr offen.',
    nextSeven: 'Die nächsten 7 Tage',
    sidebar: 'Seitenleiste',
    nothingYet: 'Noch nichts eingetragen',
    noTodos: 'Keine Aufgaben für heute.',
    moreTodos: (n: number) => `${n} weitere`,
    // a day with all-day entries only (Mike, 19.10.: "man nimmt diese gar nicht als Ganztagestermine wahr")
    onlyAllDay: 'Keine Termine mit Uhrzeit.',
    help: 'Bedienung und Hilfe',
    helpTitle: 'So bedienst du den Kalender',
    helpSwipe:
      'Wischen: vor und zurück. Im Monat blättert Wischen überall, Listen scrollen nur hoch und runter.',
    helpMonth:
      'Monat auf- und zuklappen: am Monat oder am Griff darunter ziehen – er folgt dem Finger.',
    helpWeek:
      'Woche: eine Zeile je Person. Langsam ziehen schiebt die Tage, ein schneller Wisch wechselt die Woche.',
    helpWeekNumber: 'KW-Zahl im Monat antippen öffnet diese Woche.',
    helpDay: 'Tag: Ganztägiges steht oben, zwischen Terminen siehst du die freie Zeit.',
    helpPeople: 'Wer gezeigt wird, wählst du oben rechts mit dem Personen-Knopf.',
    helpAdd: '＋ legt einen Termin an – einfach sagen oder schreiben.',
    helpKeys: 'Am PC: Pfeiltasten blättern, T springt zu heute, D/W/M/J wechselt die Ansicht.',
  },
  en: {
    today: 'Today',
    tomorrow: 'Tomorrow',
    todayLower: 'today',
    happeningNow: 'happening now',
    inMin: (n: string) => `in ${n} min`,
    inHours: (h: string) => `in ${h} hr`,
    inHoursMin: (h: string, m: string) => `in ${h} hr ${m} min`,
    inMinutesCap: (n: number) => (n === 1 ? 'In 1 minute' : `In ${n} minutes`),
    tomorrowAt: (time: string) => `Tomorrow ${time}`,
    bring: (items: string) => `Bring: ${items}`,
    pickDay: 'Choose a day',
    addEvent: 'Event',
    noEvents: 'No events – a free day.',
    nextTwoWeeks: 'The next two weeks',
    newEvent: 'New event',
    editTimetable: 'Edit timetable',
    editEvent: 'Edit event',
    allDay: 'all day',
    wholeFamily: 'Whole family',
    done: 'Done?',
    route: 'Directions',
    doneHint: 'Ticking it off earns stars – today or until tomorrow.',
    timetable: 'Timetable',
    waste: 'Bin collection',
    calendarLink: 'Calendar link',
    from: (source: string) => `From: ${source}`,
    edit: 'Edit',
    reallyDelete: 'Delete for good',
    deleteOne: (day: string) => `Delete only on ${day}`,
    deleteFollowing: (day: string) => `Delete from ${day} on`,
    deleteSeries: 'Delete the whole series',
    delete: 'Delete',
    reminder: 'Reminder',
    onlyTimes: 'Only times entered – parents can add the subjects.',
    school: 'School',
    kita: 'Nursery',
    ogs: 'After-school club',
    birthdayOf: (name: string) => `Birthday: ${name}`,
    views: 'View',
    viewDay: 'Day',
    viewWeek: 'Week',
    viewMonth: 'Month',
    viewYear: 'Year',
    back: 'Back',
    forward: 'Next',
    week: (n: number) => `Wk ${n}`,
    openWeek: (n: number) => `Open week ${n}`,
    whoWhereWhen: 'Who is where, when?',
    family: 'Family',
    me: (name: string) => `${name} (me)`,
    monthOpen: 'Expand month',
    monthClose: 'Collapse month',
    dayEvents: (day: string, n: number) => (n === 1 ? `${day}, 1 event` : `${day}, ${n} events`),
    more: (n: number) => `+${n} more`,
    freeDay: 'free',
    search: 'Search',
    searchLabel: 'Search events',
    searchHint: 'Searches all events – title, place and person.',
    searchDone: 'Done',
    noHits: (q: string) => `Nothing found for “${q}”.`,
    upcoming: 'Coming up',
    earlier: 'Earlier',
    freeWeekends: 'Free weekends',
    freeWeekendsHint: 'for everyone shown',
    weekendFree: 'Whole weekend free',
    saturdayFree: 'Saturday free',
    sundayFree: 'Sunday free',
    soon: 'Soon',
    yearHint: 'The darker a day, the busier. Yellow: school holidays. Tap a month to open it.',
    openMonth: (month: string) => `Open ${month}`,
    layers: 'What shows in the calendar?',
    layersHint: 'Each device remembers its choice – the wall display without bins, for example.',
    layerHolidays: 'School and public holidays',
    layerBirthdays: 'Birthdays',
    layerSchool: 'School and nursery',
    layerShifts: 'Shifts',
    shift: 'Shift',
    vacation: 'Holiday',
    oneOffJob: 'Task',
    shiftOnlyToday: 'This day only',
    shiftOnlyTodayHint: 'Swapped a shift, off sick or on holiday? The rhythm simply carries on.',
    shiftFree: 'Off',
    shiftAsPlanned: 'As in the rhythm',
    shiftEditPlan: 'Change the shift plan',
    shiftEndPlan: 'End the plan from here',
    shiftSwapped: (planned: string) => `Swapped – the rhythm says: ${planned}`,
    layerWaste: 'Bin collection',
    layerSubscriptions: 'Subscribed calendars',
    noRegion: 'School holidays are missing – choose your home town again under Settings → Family.',
    toSettings: 'Go to settings',
    todayTodo: 'To do today',
    allTasks: 'All tasks',
    nothingTodo: 'Nothing left to do today.',
    nextSeven: 'The next 7 days',
    sidebar: 'Sidebar',
    nothingYet: 'Nothing planned yet',
    noTodos: 'No tasks for today.',
    moreTodos: (n: number) => `${n} more`,
    onlyAllDay: 'No events with a time.',
    help: 'How it works',
    helpTitle: 'How to use the calendar',
    helpSwipe:
      'Swipe: back and forth. In the month, swiping turns the month anywhere; lists only scroll up and down.',
    helpMonth:
      'Expand or collapse the month: drag the month or the handle below – it follows your finger.',
    helpWeek:
      'Week: one row per person. Drag slowly to move the days, flick quickly to change the week.',
    helpWeekNumber: 'Tap a week number in the month to open that week.',
    helpDay: 'Day: all-day items sit on top, and you see how much time is free between events.',
    helpPeople: 'Choose who is shown with the person button at the top right.',
    helpAdd: '＋ adds an event – just say or type it.',
    helpKeys: 'On a computer: arrow keys turn pages, T jumps to today, D/W/M/Y switch the view.',
  },
} as const;

export const calendarText = CALENDAR_TEXT[locale];
